import React from "react";
import { motion } from "framer-motion";
import { Link as RouterLink, Navigate, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle } from "lucide-react";
import NavBar from "../components/NavBar";
import FooterSection from "../components/FooterSection";
import HeroSection from "../components/HeroSection";
import {
  Breadcrumbs,
  CallToAction,
  DeliverablesAndStandards,
  Faqs,
  LinkGrid,
  Process,
  SectionHeading,
  fadeUp,
} from "../components/ServiceSections";
import data from "../data.json";

// Study wording by default; services that aren't studies override it in details.pageLabels
const defaultLabels = {
  whenNeeded: "When You Need This Study",
  scopeTitle: "What We Analyze",
  scopeDescription:
    "Every study is scoped to your system, but these are the core areas we evaluate.",
  processTitle: "How We Perform the Study",
  processDescription:
    "A clear, repeatable method that produces results you can rely on and act on.",
  related: "Related Studies",
};

const Overview = ({ page, labels }) => {
  const relatedService = data.servicesList.find(
    (service) => service.slug === page.relatedService
  );
  // Optionally link straight to one study page within the related service
  const relatedStudy = relatedService?.details?.offerings?.find(
    (item) => item.slug === page.relatedStudy && item.page
  );
  const related = relatedStudy
    ? {
        to: `/services/${relatedService.slug}/${relatedStudy.slug}`,
        label: relatedStudy.title,
      }
    : relatedService && {
        to: `/services/${relatedService.slug}`,
        label: relatedService.heading,
      };

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4 max-w-6xl flex flex-col lg:flex-row gap-12 items-start">
        <motion.div {...fadeUp} className="w-full lg:w-3/5">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Overview</h2>
          {page.overview.map((paragraph, index) => (
            <p key={index} className="text-lg text-gray-600 mb-4">
              {paragraph}
            </p>
          ))}
          {related && (
            <RouterLink
              to={related.to}
              className="inline-flex items-center mt-2 font-semibold text-custom-blue hover:text-custom-red transition-colors duration-300"
            >
              See also: {related.label}
              <ArrowRight className="w-4 h-4 ml-2" />
            </RouterLink>
          )}
        </motion.div>
        <motion.div {...fadeUp} className="w-full lg:w-2/5">
          <div className="bg-gray-50 rounded-lg border-l-4 border-custom-blue p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              {labels.whenNeeded}
            </h3>
            <ul className="space-y-3">
              {page.whenNeeded.map((item, index) => (
                <li key={index} className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-custom-blue mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const Scope = ({ items, title, description }) => (
  <section className="py-16 bg-gray-50">
    <div className="container mx-auto px-4 max-w-6xl">
      <SectionHeading title={title} description={description} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((item, index) => (
          <motion.div
            key={index}
            {...fadeUp}
            transition={{ duration: 0.5, delay: (index % 3) * 0.1 }}
            className="bg-white rounded-lg shadow-lg p-6 border-t-4 border-custom-blue hover:shadow-xl transition-shadow duration-300"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {item.title}
            </h3>
            <p className="text-gray-600">{item.description}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const StudyDetail = () => {
  const { slug, studySlug } = useParams();
  const service = data.servicesList.find((item) => item.slug === slug);
  const offerings = service?.details?.offerings || [];
  const study = offerings.find((item) => item.slug === studySlug && item.page);

  if (!service) {
    return <Navigate to="/services" replace />;
  }
  if (!study) {
    return <Navigate to={`/services/${service.slug}`} replace />;
  }

  const { page } = study;
  const labels = { ...defaultLabels, ...service.details.pageLabels };
  const relatedStudies = offerings
    .filter((item) => item.page && item.slug !== study.slug)
    .map((item) => ({
      to: `/services/${service.slug}/${item.slug}`,
      label: item.title,
    }));

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />

      <HeroSection
        backgroundImage={require(`../assets/images/${study.image}`)}
        heading={study.title}
        description={page.tagline}
      />
      <Breadcrumbs
        items={[
          { label: "Home", to: "/" },
          { label: "Services", to: "/services" },
          { label: service.heading, to: `/services/${service.slug}` },
          { label: study.title },
        ]}
      />

      <Overview page={page} labels={labels} />
      <Scope
        items={page.scope}
        title={labels.scopeTitle}
        description={labels.scopeDescription}
      />
      <Process
        steps={page.process}
        title={labels.processTitle}
        description={labels.processDescription}
      />
      <DeliverablesAndStandards
        deliverables={page.deliverables}
        standards={page.standards}
      />
      <Faqs faqs={page.faqs} />
      <CallToAction title={`Let's Discuss Your ${study.title}`} />
      <LinkGrid
        title={labels.related}
        links={[
          ...relatedStudies,
          { to: `/services/${service.slug}`, label: `All ${service.heading}` },
        ]}
      />

      <FooterSection />
    </div>
  );
};

export default StudyDetail;
